import React, { useState, useRef, useEffect } from 'react';
import { Reveal } from '../components/Reveal';
import {
  Heart, Droplet, Sparkles, Award, Check, ArrowRight, Receipt, Shield,
  Users, Gift, Wallet, Megaphone, Zap, ShieldCheck, ClipboardCheck, Building2,
  FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { API } from '../lib/api';
import { reportConversion } from '../lib/gtag';
import { useSection, resolveImage } from '../contexts/ContentContext';

const ICON_MAP = {
  Heart, Droplet, Sparkles, Award, Receipt, Shield, Users, Gift,
  Wallet, Megaphone, Zap, ShieldCheck, ClipboardCheck, Building2, FileText,
  Check,
};

/* ---------------- DonationForm (Particulier / Entreprise) ---------------- */
function DonationForm({ variant, tiers }) {
  const isCompany = variant === 'entreprise';
  const popularIndex = Math.max(0, tiers.findIndex((t) => t.popular));
  const [selected, setSelected] = useState(tiers[popularIndex] || tiers[0] || { amount: 100, label: '' });
  const [custom, setCustom] = useState('');
  const [frequency, setFrequency] = useState('once');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const finalAmount = custom ? parseFloat(custom) || 0 : (selected.amount || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (finalAmount <= 0) {
      toast({ title: 'Montant invalide', description: 'Veuillez saisir un montant supérieur à 0.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        amount: finalAmount,
        frequency,
        donorType: variant,
        name,
        email,
        originUrl: window.location.origin,
        ...(isCompany ? { companyName, siret } : {}),
      });
      if (data.url) {
        // Google Ads conversion tracking : envoie l'événement AVANT la redirection vers Stripe.
        // Si gtag n'est pas chargé (consentement refusé), on redirige immédiatement.
        reportConversion(
          'AW-586557208/ymevCM_5xZkdEJjO2JcC',
          { value: finalAmount, currency: 'EUR', transactionId: data.session_id || '' },
          () => { window.location.href = data.url; }
        );
      } else {
        throw new Error('Aucune URL de paiement reçue');
      }
    } catch (err) {
      toast({
        title: 'Erreur de paiement',
        description: err?.response?.data?.detail || "Impossible d'initier le paiement. Réessayez.",
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const accent = isCompany ? 'amber' : 'sky';

  return (
    <div className={`bg-gradient-to-br from-white to-${accent}-50/40 rounded-3xl border border-${accent}-100 p-6 lg:p-8 shadow-sm`}>
      <div className="flex items-center gap-2 mb-2">
        {isCompany
          ? <Building2 className="w-5 h-5 text-amber-600" />
          : <Users className="w-5 h-5 text-sky-600" />}
        <span className={`text-xs font-bold uppercase tracking-wider ${isCompany ? 'text-amber-700' : 'text-sky-700'}`}>
          {isCompany ? 'Entreprise' : 'Particulier'}
        </span>
      </div>
      <h3 className="font-display font-extrabold text-2xl text-sky-950 mb-1">Faire un don</h3>
      <p className="text-sm text-slate-500 mb-6">Choisissez le montant et la fréquence qui vous conviennent.</p>

      {/* Frequency toggle */}
      <div className="flex gap-2 mb-6 p-1 bg-sky-50 rounded-2xl">
        <button
          type="button"
          onClick={() => setFrequency('once')}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${frequency === 'once' ? 'bg-white text-sky-900 shadow' : 'text-sky-700'}`}
          data-testid={`donate-${variant}-freq-once`}
        >
          Don unique
        </button>
        <button
          type="button"
          onClick={() => setFrequency('monthly')}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${frequency === 'monthly' ? 'bg-white text-sky-900 shadow' : 'text-sky-700'}`}
          data-testid={`donate-${variant}-freq-monthly`}
        >
          Mensuel
        </button>
      </div>

      {/* Tiers */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {tiers.map((t, i) => {
          const Icon = ICON_MAP[t.icon] || Heart;
          const active = selected.amount === t.amount && !custom;
          const activeCls = isCompany
            ? 'border-amber-500 bg-amber-50'
            : 'border-sky-500 bg-sky-50';
          return (
            <button
              key={i}
              type="button"
              onClick={() => { setSelected(t); setCustom(''); }}
              className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                active ? activeCls : `border-${accent}-100 hover:border-${accent}-300 bg-white`
              }`}
              data-testid={`donate-${variant}-tier-${t.amount}`}
            >
              {t.popular && <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider">Populaire</span>}
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${active ? (isCompany ? 'text-amber-700' : 'text-sky-700') : `text-${accent}-500`}`} />
                {active && <Check className={`w-4 h-4 ${isCompany ? 'text-amber-700' : 'text-sky-700'}`} />}
              </div>
              <div className="font-display font-extrabold text-2xl text-sky-950">
                {t.amount} €{frequency === 'monthly' && <span className="text-xs font-normal text-slate-500">/mois</span>}
              </div>
              <p className="text-xs text-slate-500 mt-1">{t.label}</p>
            </button>
          );
        })}
      </div>

      {/* Custom amount */}
      <div className="mb-6">
        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Ou montant libre</label>
        <div className="relative mt-2">
          <Input
            type="number" min="1" placeholder="Ex: 75" value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className={`h-12 pr-10 rounded-xl border-${accent}-200 text-base font-semibold`}
            data-testid={`donate-${variant}-custom`}
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">€</span>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {isCompany && (
          <>
            <Input required placeholder="Nom de l'entreprise" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="h-12 rounded-xl border-amber-200" data-testid="donate-entreprise-company" />
            <Input required placeholder="Numéro de SIRET" value={siret} onChange={(e) => setSiret(e.target.value)} className="h-12 rounded-xl border-amber-200" data-testid="donate-entreprise-siret" />
          </>
        )}
        <Input required placeholder="Prénom Nom" value={name} onChange={(e) => setName(e.target.value)} className={`h-12 rounded-xl border-${accent}-200`} data-testid={`donate-${variant}-name`} />
        <Input required type="email" placeholder="votre@email.fr" value={email} onChange={(e) => setEmail(e.target.value)} className={`h-12 rounded-xl border-${accent}-200`} data-testid={`donate-${variant}-email`} />
        <Button
          type="submit"
          disabled={loading}
          className={`w-full h-12 text-white font-bold rounded-xl border-0 disabled:opacity-70 ${
            isCompany
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
              : 'bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600'
          }`}
          data-testid={`donate-${variant}-submit`}
        >
          {loading ? 'Redirection…' : (
            <>
              Donner {finalAmount} € {frequency === 'monthly' && '/mois'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      </form>
      <p className="text-xs text-slate-500 text-center mt-3">
        Paiement sécurisé · Reçu fiscal automatique · Annulation du don mensuel à tout moment
      </p>
    </div>
  );
}

/* ---------------- Donate Page ---------------- */
export default function Donate() {
  const don = useSection('donate');
  const support = useSection('support');
  const settings = useSection('settings');
  const formsRef = useRef(null);
  const { toast } = useToast();
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'pending' | 'failed' | 'cancelled' | null

  const tiers = don.tiers || [];
  const trust = don.trust || [];
  const faqs = don.faqs || [];
  const hero = don.hero || {};
  const financial = support.financial || {};
  const success = support.success || {};

  // Handle Stripe return: poll payment status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    const cancelled = params.get('cancelled');

    if (cancelled === '1') {
      setPaymentStatus('cancelled');
      toast({ title: 'Paiement annulé', description: 'Vous pouvez réessayer à tout moment.' });
      window.history.replaceState({}, '', '/soutenir');
      return;
    }

    if (!sid) return;
    setPaymentStatus('pending');

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          if (typeof window.gtag === 'function' && process.env.REACT_APP_GADS_PURCHASE_SEND_TO) {
            window.gtag('event', 'conversion', {
              send_to: process.env.REACT_APP_GADS_PURCHASE_SEND_TO,
              value: Number(data.amount) || 1.0,
              currency: 'EUR',
              transaction_id: sid,
            });
          }
          toast({
            title: 'Merci pour votre don !',
            description: `Don de ${data.amount} € confirmé. Un reçu fiscal vous sera envoyé par e-mail.`,
          });
          window.history.replaceState({}, '', '/soutenir');
          return;
        }
        if (data.status === 'expired') {
          setPaymentStatus('failed');
          toast({ title: 'Session expirée', description: 'Merci de réessayer.', variant: 'destructive' });
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) setTimeout(poll, interval);
        else setPaymentStatus('failed');
      } catch {
        attempts += 1;
        if (attempts < maxAttempts) setTimeout(poll, interval);
        else setPaymentStatus('failed');
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToForms = () => {
    formsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* Payment status banner */}
      {paymentStatus && (
        <div className="pt-24 pb-2 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {paymentStatus === 'success' && (
              <div className="p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-200 flex items-center gap-4" data-testid="payment-success-banner">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-display font-bold text-emerald-900">Merci pour votre don !</p>
                  <p className="text-sm text-emerald-700 don-confirme" data-testid="payment-success-text">Votre paiement a bien été confirmé. Un reçu fiscal vous sera envoyé par e-mail.</p>
                </div>
              </div>
            )}
            {paymentStatus === 'pending' && (
              <div className="p-5 rounded-2xl bg-sky-50 border-2 border-sky-200 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-sky-500 flex items-center justify-center shrink-0 animate-pulse">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-display font-bold text-sky-900">Vérification du paiement…</p>
                  <p className="text-sm text-sky-700">Merci de patienter quelques secondes.</p>
                </div>
              </div>
            )}
            {paymentStatus === 'cancelled' && (
              <div className="p-5 rounded-2xl bg-amber-50 border-2 border-amber-200 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-display font-bold text-amber-900">Paiement annulé</p>
                  <p className="text-sm text-amber-700">Vous pouvez réessayer à tout moment.</p>
                </div>
              </div>
            )}
            {paymentStatus === 'failed' && (
              <div className="p-5 rounded-2xl bg-rose-50 border-2 border-rose-200 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center shrink-0">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-display font-bold text-rose-900">Impossible de confirmer le paiement</p>
                  <p className="text-sm text-rose-700">Contactez-nous si le montant a été prélevé.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {hero.badge && (
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-5">
                <Heart className="w-3.5 h-3.5 fill-emerald-700" />{hero.badge}
              </div>
            )}
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-6 leading-tight">
              {hero.title} <span className="gradient-text">{hero.titleHighlight}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">{hero.description}</p>
          </Reveal>
        </div>
      </section>

      {/* NOS BESOINS — section supprimée à la demande de l'utilisateur */}

      {/* BESOINS FINANCIERS */}
      <section className="pb-20 bg-gradient-to-br from-amber-50/40 via-white to-emerald-50/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <div className="lg:sticky lg:top-32">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-5 shadow-lg shadow-amber-500/30">
                  <Wallet className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-bold text-3xl lg:text-4xl text-sky-950 mb-4">
                  Nos besoins <span className="text-amber-600">financiers</span>
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {financial.intro || "Le coût public d'un Kiosque à Eau, livré et installé, est d'environ 42 000 €."}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Coût public</p>
                    <p className="font-display font-extrabold text-3xl text-slate-800">42 000 €</p>
                    <p className="text-xs text-slate-500 mt-1">livré + installé</p>
                  </div>
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white shadow-lg shadow-emerald-500/20">
                    <p className="text-xs uppercase tracking-wider text-emerald-100 mb-1">Réalisé par nos soins</p>
                    <p className="font-display font-extrabold text-3xl">21 000 €</p>
                    <p className="text-xs text-emerald-100 mt-1">Kiosque de Lons</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="space-y-5">
              <Reveal delay={100}>
                <div className="p-6 rounded-3xl bg-white border border-amber-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-display font-bold text-lg text-sky-950">+ 4 000 €</p>
                      <p className="text-xs text-slate-500">consacrés à</p>
                    </div>
                  </div>
                  <ul className="space-y-2.5 text-sm text-slate-700">
                    {(financial.extra || []).map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={200}>
                <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-50 to-white border border-sky-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-sky-600" />
                    </div>
                    <p className="font-display font-bold text-lg text-sky-950">
                      Nos frais de fonctionnement restent volontairement très limités
                    </p>
                  </div>
                  <ul className="space-y-2.5 text-sm text-slate-700">
                    {(financial.operatingCosts || []).map((item, i) => (
                      <li key={i} className="flex gap-2 py-1 border-b border-sky-50 last:border-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0 mt-2"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={300}>
                <div className="p-5 rounded-2xl bg-emerald-50 border-l-4 border-emerald-500">
                  <p className="text-sm text-emerald-900 leading-relaxed">
                    <ShieldCheck className="w-4 h-4 inline mr-2 -mt-0.5 text-emerald-600" />
                    {financial.note}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* RÉUSSIR GRÂCE À VOTRE DON — 3 raisons */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 text-xs font-bold uppercase tracking-wider text-emerald-700 mb-4">
                Passer à l'action
              </span>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-6">
                Réussir grâce à <span className="gradient-text">votre don</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                {success.text1 || "Chaque don contribue concrètement au développement des Kiosques à Eau et à la réduction des bouteilles plastiques."}
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="p-8 lg:p-10 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-100">
              <p className="font-display font-bold text-xl text-sky-950 mb-6 text-center">
                En faisant un don :
              </p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {(success.reasons || []).map((r, i) => {
                  const Icon = ICON_MAP[r.icon] || Check;
                  return (
                    <div key={i} className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm text-center">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-500 mx-auto flex items-center justify-center mb-3 shadow-md">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{r.text}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-center font-display font-bold text-lg text-emerald-700">
                {success.thankYou}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PARTICULIER + ENTREPRISE (cards fiscales) */}
      <section className="pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Particulier card */}
            <Reveal>
              <div className="h-full p-8 rounded-3xl bg-white shadow-lg border border-sky-100 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-40 h-40 bg-sky-100/60 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-sky-500/20">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-wider text-sky-600 font-bold">Réduction d'impôt</span>
                      <h3 className="font-display font-bold text-2xl text-sky-950">Vous êtes un particulier</h3>
                    </div>
                  </div>
                  <p className="text-slate-700 text-sm mb-5">{success.particulier?.intro}</p>

                  <div className="rounded-2xl bg-gradient-to-br from-sky-50 to-indigo-50 border border-sky-100 p-5 mb-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-sky-700 mb-3">Exemple concret</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-xl bg-white">
                        <span className="text-sm text-slate-600">Vous donnez</span>
                        <span className="font-display font-bold text-xl text-sky-900">{success.particulier?.exampleDonation || '100 €'}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50">
                        <span className="text-sm text-emerald-700 font-medium">Réduction d'impôt (66%)</span>
                        <span className="font-display font-bold text-xl text-emerald-700">− {success.particulier?.exampleReduction || '66 €'}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-sky-500 text-white shadow-md">
                        <span className="text-sm font-semibold">Coût réel pour vous</span>
                        <span className="font-display font-extrabold text-2xl">{success.particulier?.exampleReal || '34 €'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-xs text-slate-600 leading-relaxed">
                    <p className="flex gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{success.particulier?.note}</span>
                    </p>
                    <p className="flex gap-2">
                      <FileText className="w-4 h-4 text-sky-500 shrink-0 mt-0.5" />
                      <span>{success.particulier?.receipt}</span>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Entreprise card */}
            <Reveal delay={100}>
              <div className="h-full p-8 rounded-3xl bg-white shadow-lg border border-amber-100 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-40 h-40 bg-amber-100/60 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-wider text-amber-600 font-bold">Mécénat d'entreprise</span>
                      <h3 className="font-display font-bold text-2xl text-sky-950">Vous êtes une entreprise</h3>
                    </div>
                  </div>
                  <p className="text-slate-700 text-sm mb-5">{success.entreprise?.intro}</p>

                  <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 p-5 mb-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-3">Exemple concret</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-xl bg-white">
                        <span className="text-sm text-slate-600">Votre entreprise verse</span>
                        <span className="font-display font-bold text-xl text-amber-900">{success.entreprise?.exampleDonation || '100 €'}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md">
                        <span className="text-sm font-semibold">Réduction d'impôt (60%)</span>
                        <span className="font-display font-extrabold text-2xl">{success.entreprise?.exampleReduction || '60 €'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-xs text-slate-600 leading-relaxed">
                    <p className="flex gap-2">
                      <FileText className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <span>{success.entreprise?.receipt}</span>
                    </p>
                    <p className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-amber-900 leading-relaxed">
                      {success.entreprise?.extra}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRÊT À FAIRE LA DIFFÉRENCE — Dark banner */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-sky-950 to-emerald-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Reveal>
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur mx-auto flex items-center justify-center mb-6">
              <Heart className="w-8 h-8 text-emerald-300" />
            </div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl mb-6">
              Prêt à faire la <span className="text-emerald-300">différence ?</span>
            </h2>
            <p className="text-base text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto">
              {success.text2 || "Parce que l'association Kiosques à Eau est reconnue d'intérêt général, vos dons ouvrent droit à une réduction d'impôt."}
            </p>
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 backdrop-blur mb-10">
              <ShieldCheck className="w-5 h-5 text-emerald-300" />
              <span className="text-sm font-semibold text-emerald-100">
                Association reconnue d'intérêt général — Réduction d'impôt jusqu'à 66%
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToForms}
                className="water-btn bg-gradient-to-r from-emerald-400 to-sky-400 hover:from-emerald-500 hover:to-sky-500 text-slate-900 font-bold px-8 py-6 rounded-2xl border-0 shadow-2xl shadow-emerald-500/40"
                data-testid="donate-cta-scroll"
              >
                <Heart className="w-5 h-5 mr-2" />
                Je fais un don maintenant
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              {success.fiscalPdf && (
                <a href={resolveImage(success.fiscalPdf)} download target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="border-2 border-white/30 hover:border-white/60 bg-white/5 hover:bg-white/10 text-white font-semibold px-6 py-6 rounded-2xl"
                    data-testid="donate-fiscal-pdf-btn"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Consulter l'agrément fiscal
                  </Button>
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2 FORMS SIDE BY SIDE — PARTICULIER / ENTREPRISE */}
      <section ref={formsRef} className="py-20 bg-gradient-to-b from-sky-50/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="font-display font-extrabold text-4xl text-sky-950 mb-3">
                Faites votre <span className="gradient-text">don</span>
              </h2>
              <p className="text-slate-600">Choisissez le formulaire adapté à votre profil.</p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-6">
            <Reveal><DonationForm variant="particulier" tiers={tiers} /></Reveal>
            <Reveal delay={100}><DonationForm variant="entreprise" tiers={tiers} /></Reveal>
          </div>

          {/* Trust + external link */}
          <Reveal>
            <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              <div className="p-5 rounded-2xl bg-white border border-sky-100 space-y-3">
                <h3 className="font-display font-bold text-sky-950 mb-2">Pourquoi nous faire confiance ?</h3>
                {trust.map((item, i) => {
                  const Icon = ICON_MAP[item.icon] || Shield;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-emerald-700" />
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed pt-1">{item.text}</p>
                    </div>
                  );
                })}
              </div>
              {settings.donateExternalUrl && (
                <a href={settings.donateExternalUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="h-full p-5 rounded-2xl bg-gradient-to-br from-pink-50 to-orange-50 border border-pink-100 hover:border-pink-300 transition-colors flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                      <Heart className="w-6 h-6 text-rose-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-sky-950">Donner via HelloAsso</p>
                      <p className="text-xs text-slate-500">Plateforme solidaire française</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-sky-700" />
                  </div>
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">FAQ</span>
              <h2 className="font-display font-extrabold text-4xl text-sky-950">Vos questions, nos réponses</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-sky-100 px-5 data-[state=open]:shadow-md transition-shadow">
                  <AccordionTrigger className="font-display font-bold text-sky-950 text-base hover:no-underline text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
